/**
 * Module-level Web Audio graph manager for the Dashboard tiles.
 *
 * Design rules (learned the hard way):
 *   1. Once you create a `MediaElementAudioSourceNode` on a <video>, the
 *      element's audio is permanently routed through the Web Audio graph.
 *      So we build the graph ONCE per video element and never tear it down.
 *   2. The permanent chain per video is:
 *        source -> analyser -> gain -> ctx.destination
 *      `analyser` is a no-op on the signal (it just observes samples), so it
 *      can live in the chain permanently. `gain` is 0 when the channel is
 *      muted and `volume` when it's soloed.
 *   3. The AudioContext must be resumed after a user gesture. We call
 *      `resumeContext()` on each solo click.
 *   4. Graphs are cached in a WeakMap keyed by the <video> element so React
 *      never recreates them.
 */

let sharedCtx = null;
const graphs = new WeakMap();

export function ensureContext() {
  if (typeof window === "undefined") return null;
  if (!sharedCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) sharedCtx = new Ctx();
  }
  return sharedCtx;
}

export async function resumeContext() {
  const ctx = ensureContext();
  if (ctx && ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (_) {
      /* noop */
    }
  }
  return ctx;
}

export function getOrCreateGraph(videoEl) {
  if (!videoEl) return null;
  const ctx = ensureContext();
  if (!ctx) return null;
  let g = graphs.get(videoEl);
  if (g) return g;
  try {
    const source = ctx.createMediaElementSource(videoEl);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.25;
    const gain = ctx.createGain();
    gain.gain.value = 0; // start muted
    source.connect(analyser);
    analyser.connect(gain);
    gain.connect(ctx.destination);
    g = { source, analyser, gain };
    graphs.set(videoEl, g);
    return g;
  } catch (_) {
    return null;
  }
}

export function setChannelGain(videoEl, value) {
  const g = graphs.get(videoEl);
  if (!g) return;
  const ctx = ensureContext();
  const now = ctx?.currentTime || 0;
  const clamped = Math.max(0, Math.min(1, value));
  try {
    g.gain.gain.cancelScheduledValues(now);
    g.gain.gain.setTargetAtTime(clamped, now, 0.015);
  } catch (_) {
    g.gain.gain.value = clamped;
  }
}

export function getAnalyser(videoEl) {
  const g = graphs.get(videoEl);
  return g ? g.analyser : null;
}
